import { useParams, useNavigate } from 'react-router-dom';
import { clearOutbox } from '../lib/offline/outbox.js';
import { clearSavedQueries } from '../lib/offline/persist.js';
import { theme } from '../theme.js';
import { useInvitePreview, useClaimInvite, useAcceptInvite } from '../lib/queries.js';
import { useAuth } from '../context/AuthContext.js';
import { Button } from '../components/Button.js';
import { ApiError } from '../lib/api.js';
import { Logo } from '../components/Logo.js';
import { useQueryClient } from '@tanstack/react-query';
import { signOut } from '../lib/auth.js';

export function ClaimPage() {
  const { token = '' } = useParams<{ token: string }>();
  const nav = useNavigate();
  const { data: preview, isLoading } = useInvitePreview(token);
  const claimMut = useClaimInvite(token);
  const acceptMut = useAcceptInvite(token);
  const { user, switchOrg, refresh } = useAuth();
  const qc = useQueryClient();
  // Signed in as someone else (e.g. a shared device, or testing): accepting
  // can't work, so offer to sign out and carry on with this invite.
  const otherAccount = !!user && !!preview?.email && user.email.toLowerCase() !== preview.email.toLowerCase();
  // Already using the app (in another unit): join with that account rather
  // than creating one.
  const needsSignIn = claimMut.error instanceof ApiError && claimMut.error.code === 'SIGN_IN_TO_ACCEPT';
  const actionError = user ? acceptMut.error : claimMut.error;

  async function switchAccount() {
    await signOut();
    await Promise.all([clearOutbox(), clearSavedQueries()]); // nothing of theirs for the next sign-in
    qc.clear();
    await refresh(); // signed out: this same link now offers to claim or sign in
  }

  function accept() {
    acceptMut.mutate(undefined, {
      onSuccess: ({ orgId }) => { switchOrg(orgId); nav('/', { replace: true }); },
    });
  }

  return (
    <div style={{
      minHeight: '100dvh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      background: theme.bg, padding: 24,
    }}>
      <div style={{ width: '100%', maxWidth: 380, textAlign: 'center' }}>
        <div style={{ marginBottom: 24 }}><Logo size={24} /></div>

        {isLoading && <div style={{ color: theme.textMuted }}>Checking invite…</div>}

        {!isLoading && preview && !preview.valid && (
          <div style={{
            padding: '16px 20px', borderRadius: 12,
            background: theme.status.danger.bg, color: theme.status.danger.fg,
            fontSize: 14, fontWeight: 500,
          }}>
            {preview.reason ?? 'This invite link is not valid.'}
          </div>
        )}

        {!isLoading && preview?.valid && !claimMut.data && (
          <div style={{
            padding: '20px', borderRadius: 14,
            background: theme.surface, border: `1px solid ${theme.border}`,
          }}>
            <div style={{ fontSize: 15, color: theme.text, marginBottom: 4 }}>
              You're invited to join
            </div>
            <div style={{ fontSize: 20, fontWeight: 700, color: theme.text, marginBottom: 6 }}>
              {preview.orgName}
            </div>
            <div style={{ fontSize: 13, color: theme.textMuted, marginBottom: 18 }}>
              as {preview.email}
            </div>

            {actionError && !otherAccount && (
              <div style={{
                fontSize: 13, color: theme.status.danger.fg,
                background: theme.status.danger.bg,
                padding: '10px 14px', borderRadius: 8, marginBottom: 12,
              }}>
                {actionError instanceof ApiError ? actionError.message : 'Something went wrong'}
              </div>
            )}

            {otherAccount ? (
              <>
                <div style={{ fontSize: 13, color: theme.textDim, marginBottom: 12 }}>
                  You’re signed in as {user!.email}. Sign out to continue as {preview.email}.
                </div>
                <Button fullWidth size="lg" onClick={() => void switchAccount()}>
                  Sign out and continue
                </Button>
              </>
            ) : user ? (
              <Button fullWidth size="lg" loading={acceptMut.isPending} onClick={accept}>
                Join {preview.orgName}
              </Button>
            ) : needsSignIn ? (
              <Button
                fullWidth size="lg"
                onClick={() => nav(`/login?email=${encodeURIComponent(preview.email ?? '')}&next=${encodeURIComponent(`/claim/${token}`)}`)}
              >
                Sign in to accept
              </Button>
            ) : (
              <Button fullWidth size="lg" loading={claimMut.isPending} onClick={() => claimMut.mutate()}>
                Claim invite
              </Button>
            )}
          </div>
        )}

        {claimMut.data && (
          <div style={{
            padding: '20px', borderRadius: 14,
            background: theme.surface, border: `1px solid ${theme.border}`,
            textAlign: 'left',
          }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: theme.text, marginBottom: 12, textAlign: 'center' }}>
              Account created
            </div>
            <div style={{ fontSize: 12, color: theme.textDim, marginBottom: 4 }}>Email</div>
            <div style={{
              fontSize: 13, color: theme.text, background: theme.surface2,
              padding: '8px 12px', borderRadius: 8, marginBottom: 12, wordBreak: 'break-all',
            }}>
              {claimMut.data.email}
            </div>
            <div style={{ fontSize: 12, color: theme.textDim, marginBottom: 4 }}>Temporary password</div>
            <div style={{
              fontSize: 13, color: theme.text, background: theme.surface2,
              padding: '8px 12px', borderRadius: 8, marginBottom: 4, fontFamily: 'monospace', wordBreak: 'break-all',
            }}>
              {claimMut.data.tempPassword}
            </div>
            <div style={{ fontSize: 11, color: theme.textMuted, marginBottom: 18 }}>
              Shown once — you'll set your own password on first sign-in.
            </div>
            <Button
              fullWidth size="lg"
              onClick={() => nav(`/login?email=${encodeURIComponent(claimMut.data!.email)}`, { replace: true })}
            >
              Continue to sign in
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
